// frontend/src/types/scheduling.ts

/**
 * TypeScript type definitions for the Scheduling feature.
 * Matches the backend Django models and serializers.
 */

export type CalendarProvider = 'google' | 'outlook';

export type DistributionMode = 'equal' | 'maximize';

export type MeetingStatus = 'scheduled' | 'cancelled' | 'completed' | 'no_show';

export type MeetingType = 'individual' | 'team';

export interface CalendarConnection {
  id: string;
  user: number;
  user_name: string;
  provider: CalendarProvider;
  provider_display: string;
  primary_calendar_id: string;
  calendars_to_check: string[];
  is_active: boolean;
  token_expiry: string;
  is_expired: boolean;
  created_at: string;
  updated_at: string;
}

export interface WorkingHours {
  [day: string]: {
    enabled: boolean;
    start: string;  // Format: "HH:MM"
    end: string;    // Format: "HH:MM"
  };
}

export interface AvailabilitySettings {
  id?: number;
  user: number;
  user_name?: string;
  working_hours: WorkingHours;
  time_zone: string;
  default_meeting_duration: number;
  booking_window_days: number;
  minimum_notice_hours: number;
  custom_slug: string;
  meeting_title: string;
  meeting_location: string;
  welcome_message: string;
  is_enabled: boolean;
  /** Find-or-create a CRM contact for whoever books through this page. */
  create_contact: boolean;
  booking_url?: string;
  has_calendar_connected?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface TeamMember {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
}

export interface Team {
  id: string;
  name: string;
  description: string;
  slug: string;
  members: number[];  // User IDs
  members_details?: TeamMember[];
  member_count?: number;
  distribution_mode: DistributionMode;
  distribution_mode_display?: string;
  default_meeting_duration: number;
  meeting_title: string;
  meeting_location: string;
  welcome_message: string;
  is_active: boolean;
  booking_url?: string;
  created_by?: number;
  created_by_name?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ScheduledMeeting {
  id: string;
  host: number;
  host_name?: string;
  host_email?: string;
  /** CRM contact the invitee was matched to, when the booking page captures leads. */
  contact?: number | null;
  contact_name?: string | null;
  invitee_name: string;
  invitee_email: string;
  invitee_phone?: string;
  invitee_timezone: string;
  invitee_notes?: string;
  meeting_type: MeetingType;
  meeting_type_display?: string;
  team?: string;
  team_name?: string;
  title: string;
  location: string;
  start_time: string;  // ISO datetime
  end_time: string;    // ISO datetime
  duration_minutes: number;
  status: MeetingStatus;
  status_display?: string;
  calendar_event_id?: string;
  cancelled_at?: string;
  cancelled_by?: string;
  cancellation_reason?: string;
  cancellation_url?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TimeSlot {
  start_time: string;  // ISO datetime
  end_time: string;    // ISO datetime
  available: boolean;
}

export interface BookingRequest {
  invitee_name: string;
  invitee_email: string;
  invitee_phone?: string;
  invitee_timezone: string;
  invitee_notes?: string;
  start_time: string;  // ISO datetime
}

export interface PublicUserInfo {
  name: string;
  username: string;
  meeting_title: string;
  meeting_location: string;
  welcome_message: string;
  duration_minutes: number;
}

export interface AvailabilityResponse {
  user: PublicUserInfo;
  slots: TimeSlot[];
}

export interface AssignmentStats {
  member_id: number;
  member_name: string;
  member_email: string;
  scheduled: number;
  completed: number;
  cancelled: number;
  total: number;
}

export interface TeamStatsResponse {
  team_name: string;
  days: number;
  members: AssignmentStats[];
}

export interface OAuthResponse {
  oauth_url?: string;
  connected?: boolean;
  email?: string;
  provider: CalendarProvider;
}

// Form types for creating/updating
export interface AvailabilitySettingsForm {
  working_hours: WorkingHours;
  time_zone: string;
  default_meeting_duration: number;
  booking_window_days: number;
  minimum_notice_hours: number;
  custom_slug?: string;
  meeting_title: string;
  meeting_location?: string;
  welcome_message?: string;
  is_enabled: boolean;
  create_contact?: boolean;
}

export interface TeamForm {
  name: string;
  description?: string;
  slug: string;
  members?: number[];
  distribution_mode: DistributionMode;
  default_meeting_duration: number;
  meeting_title: string;
  meeting_location?: string;
  welcome_message?: string;
  is_active: boolean;
}

// Helper function to get default working hours
export const getDefaultWorkingHours = (): WorkingHours => ({
  monday: { enabled: true, start: '09:00', end: '17:00' },
  tuesday: { enabled: true, start: '09:00', end: '17:00' },
  wednesday: { enabled: true, start: '09:00', end: '17:00' },
  thursday: { enabled: true, start: '09:00', end: '17:00' },
  friday: { enabled: true, start: '09:00', end: '17:00' },
  saturday: { enabled: false, start: '09:00', end: '17:00' },
  sunday: { enabled: false, start: '09:00', end: '17:00' },
});

// Helper to format time slot for display
export const formatTimeSlot = (slot: TimeSlot): string => {
  const start = new Date(slot.start_time);
  const end = new Date(slot.end_time);
  const options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
  return `${start.toLocaleTimeString('en-US', options)} - ${end.toLocaleTimeString('en-US', options)}`;
};

// Helper to group slots by date
export const groupSlotsByDate = (slots: TimeSlot[]): Record<string, TimeSlot[]> => {
  const grouped: Record<string, TimeSlot[]> = {};
  slots.forEach(slot => {
    const date = new Date(slot.start_time).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(slot);
  });
  return grouped;
};
